import React, { useState } from 'react';
import { v4 } from 'uuid';
import {
  BarcodeExportConfigurationEntry,
  BarcodeExportConfigurationField,
  BarcodeExportConfigurationFormat,
  BarcodeExportConfigurationInput,
} from '../../../../types/barcode_export_configuration';
import { barcodeExportConfigurationEntityTypeFieldMap } from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { CancelButton, DeleteButton, SaveButton } from '../../../components/Button';
import { DropdownSelect } from '../../../components/Dropdown';
import { ActionModal } from '../../../components/Modal';
import { TextInput } from '../../../components/TextInput';

interface BarcodeExportConfigurationEntryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  configuration: BarcodeExportConfigurationInput;
  entry: BarcodeExportConfigurationEntry | null;
  onSave: (entry: BarcodeExportConfigurationEntry) => void;
  onDelete: (entryId: string) => void;
}

export default function BarcodeExportConfigurationEntryModal({
  open,
  setOpen,
  entry,
  configuration,
  onSave,
  onDelete,
}: BarcodeExportConfigurationEntryModalProps) {
  const validFields = barcodeExportConfigurationEntityTypeFieldMap[configuration.entityType];

  const [input, setInput] = useState<BarcodeExportConfigurationEntry>(
    entry || {
      id: v4(),
      format: BarcodeExportConfigurationFormat.code128,
      field: validFields[0],
      height: 32.75,
      horizontalMargin: 2,
      verticalMargin: 2,
      includeText: true,
    },
  );

  const footer = (
    <div className='flex justify-between'>
      {entry ? <DeleteButton onClick={() => onDelete(entry.id)} /> : <div></div>}
      <div className='flex gap-2'>
        <CancelButton onClick={() => setOpen(false)} />
        <SaveButton onClick={() => onSave(input)} />
      </div>
    </div>
  );

  return (
    <ActionModal title={'Barcode generation entry'} open={open} setOpen={setOpen} footer={footer}>
      <div className='grid gap-2'>
        <div className='grid grid-cols-2 gap-2'>
          <DropdownSelect<BarcodeExportConfigurationField>
            label={translate({ key: TranslationKey.field })}
            value={input.field}
            textBuilder={item => translate({ key: item })}
            items={[...validFields]}
            onSelect={field => setInput({ ...input, field })}
          />
          <DropdownSelect
            label={translate({ key: TranslationKey.format })}
            value={input.format}
            textBuilder={item => translate({ key: item })}
            items={Object.values(BarcodeExportConfigurationFormat)}
            onSelect={format => setInput({ ...input, format })}
          />
          <TextInput
            label={`${translate({ key: TranslationKey.height })} (mm)`}
            value={input.height}
            onChange={value => setInput({ ...input, height: Number(value) })}
          />
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <TextInput
            label={`${translate({ key: TranslationKey.horizontalMargin })} (mm)`}
            value={input.horizontalMargin}
            onChange={value => setInput({ ...input, horizontalMargin: Number(value) })}
            placeholder={translate({ key: TranslationKey.horizontalMargin })}
          />
          <TextInput
            label={`${translate({ key: TranslationKey.verticalMargin })} (mm)`}
            value={input.verticalMargin}
            onChange={value => setInput({ ...input, verticalMargin: Number(value) })}
            placeholder={translate({ key: TranslationKey.verticalMargin })}
          />
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <TextInput
            label={translate({ key: TranslationKey.prefix })}
            value={input.prefix || ''}
            onChange={value => setInput({ ...input, prefix: value })}
            placeholder={translate({ key: TranslationKey.prefix })}
          />
          <TextInput
            label={translate({ key: TranslationKey.suffix })}
            value={input.suffix || ''}
            onChange={value => setInput({ ...input, suffix: value })}
            placeholder={translate({ key: TranslationKey.suffix })}
          />
        </div>
      </div>
    </ActionModal>
  );
}
